/**
 * ISO2 codes where a leading 0 after the dial code is a significant digit
 * retained in the international format — i.e. NOT a droppable national trunk
 * prefix. Italian fixed lines keep the leading 0 (e.g. "+39 06 …"), so the
 * trunk-zero strip must not touch them. Extend if other such countries surface.
 * @type {Set<string>}
 */
const TRUNK_ZERO_RETAINED = new Set(["it"]);

/**
 * Auto-format a stored phone value into editable international (E.164) form.
 *
 * react-phone-input-2 locks the dial code (countryCodeEditable=false), so a
 * value that does not begin with a valid country code — e.g. a national number
 * saved with a bare "+", "+07939343002", or a raw "07939343002" — loads frozen
 * and cannot be edited. A country code never starts with 0, so a leading 0 is a
 * national trunk prefix: we drop it and anchor to the UK code (+44) so the field
 * becomes editable. This assumes UK, which is why the caller surfaces a notice
 * and the agent can change the country via the dropdown if it is not UK.
 *
 * @param {string} raw - Stored phone value.
 * @returns {{ value: string, formatted: boolean }} The (possibly) reformatted
 *   value and whether a change was made.
 */
export const autoFormatGbPhone = (raw) => {
  const str = String(raw ?? "").trim();
  const digits = str.replace(/\D/g, "");
  if (!digits) return { value: str, formatted: false };
  // Already valid international (has a country code, not a trunk 0).
  if (str.startsWith("+") && !digits.startsWith("0")) {
    return { value: str, formatted: false };
  }
  // Malformed: national / bare-"+" number carrying a trunk 0 -> anchor to +44.
  if (digits.startsWith("0")) {
    return { value: "+44" + digits.replace(/^0+/, ""), formatted: true };
  }
  return { value: str, formatted: false };
};

/**
 * Strip a trunk 0 typed directly after the dial code (e.g. "+44 0 7939…"),
 * except for countries in {@link TRUNK_ZERO_RETAINED} (e.g. Italy) whose leading
 * 0 is significant. Used on typing; the dial code itself stays dropdown-only.
 *
 * @param {string} value - Digits emitted by react-phone-input-2 (no leading "+").
 * @param {string} [dialCode] - Dial code of the selected country (e.g. "44").
 * @param {string} [iso2] - ISO2 code of the selected country (e.g. "gb", "it").
 * @returns {string}
 */
export const stripTrunkZero = (value, dialCode, iso2) => {
  if (!dialCode) return value;
  if (
    value.startsWith(dialCode + "0") &&
    !TRUNK_ZERO_RETAINED.has(String(iso2 || "").toLowerCase())
  ) {
    return dialCode + value.slice(dialCode.length + 1);
  }
  return value;
};
